import { useEffect, useRef, useState } from "react";
import type { Html5Qrcode as Html5QrcodeType } from "html5-qrcode";

interface Props {
  onDecoded: (code: string) => void;
  /** Called when the camera can't start (denied permission, no device, etc).
      Parent should swap to photo fallback. If omitted, scanner just shows
      the error in-place. */
  onCameraUnavailable?: (reason: CameraErrorKind) => void;
}

export type CameraErrorKind = "denied" | "no-device" | "insecure" | "unknown";

const READER_ID = "live-barcode-reader";

export default function LiveBarcodeScanner({ onDecoded, onCameraUnavailable }: Props) {
  // Hold callbacks in refs so the effect doesn't depend on their identity
  // (otherwise a new arrow function each render would re-mount the camera).
  const onDecodedRef = useRef(onDecoded);
  onDecodedRef.current = onDecoded;
  const onUnavailableRef = useRef(onCameraUnavailable);
  onUnavailableRef.current = onCameraUnavailable;

  const [error, setError] = useState<{ kind: CameraErrorKind; message: string } | null>(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let scanner: Html5QrcodeType | null = null;
    let started = false;
    let stopped = false;
    let decoded = false;

    // html5-qrcode's stop() throws synchronously (as a plain string) when the
    // scanner isn't in the running state — so a .catch() won't catch it.
    // Wrap in try/catch and use the stopped flag to prevent double-stop races
    // between the decode callback and React unmount cleanup.
    const safeStop = async (s: Html5QrcodeType | null) => {
      if (!s || stopped) return;
      stopped = true;
      try {
        await s.stop();
      } catch {
        // sync-throw swallowed (e.g. "Cannot stop, scanner is not running")
      }
      try {
        s.clear();
      } catch {
        // clear can also throw if the scanner wasn't fully initialized
      }
    };

    (async () => {
      try {
        // Lazy-load html5-qrcode (~200KB) so it doesn't bloat the initial bundle.
        const { Html5Qrcode } = await import("html5-qrcode");
        scanner = new Html5Qrcode(READER_ID, { verbose: false });
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 280, height: 160 }, aspectRatio: 1 },
          (decodedText) => {
            if (decoded) return;
            decoded = true;
            void safeStop(scanner);
            onDecodedRef.current(decodedText);
          },
          () => {
            // per-frame failures fire constantly — ignore
          }
        );
        started = true;
        if (cancelled) {
          await safeStop(scanner);
          return;
        }
        setStarting(false);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        let kind: CameraErrorKind;
        let display: string;
        if (msg.includes("Permission") || msg.includes("NotAllowed")) {
          kind = "denied";
          display = "Camera access was denied.";
        } else if (msg.includes("NotFound") || msg.includes("Requested device")) {
          kind = "no-device";
          display = "No camera found on this device.";
        } else if (msg.includes("secure") || msg.includes("https")) {
          kind = "insecure";
          display = "Live scanning needs a secure connection (HTTPS).";
        } else {
          kind = "unknown";
          display = msg;
        }
        setError({ kind, message: display });
        setStarting(false);
        onUnavailableRef.current?.(kind);
      }
    })();

    return () => {
      cancelled = true;
      if (scanner && started) {
        void safeStop(scanner);
      }
    };
  }, []);

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-semibold">Scan a barcode</h1>
      <p className="text-sm text-slate-400">
        Hold the barcode flat in the frame. Auto-detects when it locks on.
      </p>

      <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-black border border-slate-800">
        <div id={READER_ID} className="w-full h-full" />
        {starting && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 pointer-events-none">
            Starting camera...
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm space-y-2">
          <div>{error.message}</div>
          {error.kind === "denied" && (
            <div className="text-xs text-red-300/90">
              To enable: <strong>iOS Settings → Safari → Camera & Microphone → Allow</strong>, then reload.
              On Chrome/Android, tap the camera icon in the address bar.
            </div>
          )}
          {error.kind === "insecure" && (
            <div className="text-xs text-red-300/90">
              The deployed app uses HTTPS by default — this only happens in local dev over HTTP. Run <code className="bg-red-950 px-1">npm run dev:https</code> instead.
            </div>
          )}
          {/* Fallback CTA — without this the user is stuck. The parent
              (BarcodeCapture) listens for the same signal via onCameraUnavailable
              and offers a switch; this button is the in-error mirror of that. */}
          {(error.kind === "denied" || error.kind === "no-device") && onUnavailableRef.current && (
            <div className="text-xs text-red-200/90 pt-1">
              Switching you to photo capture — snap a photo of the barcode instead.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
